import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { X } from "lucide-react";
import { useLang, API, fileUrl } from "@/context/LanguageContext";
import { useSEO } from "@/components/seo";
import { Reveal, SectionHead } from "@/components/motion";

export default function Gallery() {
  const { t, lang } = useLang();
  useSEO("gallery");
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);

  useEffect(() => {
    axios.get(`${API}/gallery`).then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const filtered = filter === "all" ? items : items.filter((i) => i.category === filter);

  return (
    <div className="bg-anthracite-dark pt-16" data-testid="gallery-page">
      <section className="border-b border-white/10 bg-anthracite grid-lines-dark">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:py-28">
          <SectionHead kicker={t.galleryPage.kicker} title={t.galleryPage.title} light testid="gallery" />
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-2xl text-slate-400">{t.galleryPage.intro}</p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8">
        <div className="flex flex-wrap gap-2" data-testid="gallery-filters">
          {["all", "drehen", "fraesen", "druck"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`border px-5 py-2.5 font-mono-tech text-xs uppercase tracking-widest transition-colors ${
                filter === f ? "border-royal bg-royal text-white" : "border-white/15 text-slate-300 hover:border-royal-soft hover:text-white"
              }`}
              data-testid={`gallery-filter-${f}`}
            >
              {t.galleryPage.filters[f]}
            </button>
          ))}
        </div>

        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((item, i) => (
            <Reveal key={item.id} delay={(i % 3) * 0.06}>
              <button
                onClick={() => setActive(item)}
                className="group relative block w-full overflow-hidden border border-white/10 text-left"
                data-testid={`gallery-item-${i}`}
              >
                <img
                  src={fileUrl(item.image_url)}
                  alt={lang === "de" ? item.title_de : item.title_it}
                  className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-anthracite-dark/90 via-transparent to-transparent" />
                {item.is_placeholder && (
                  <span className="absolute right-3 top-3 border border-amber-400/50 bg-anthracite-dark/80 px-2 py-1 font-mono-tech text-[10px] uppercase tracking-widest text-amber-300" data-testid={`gallery-placeholder-badge-${i}`}>
                    {t.galleryPage.placeholderBadge}
                  </span>
                )}
                <div className="absolute bottom-0 p-5">
                  <p className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-royal-soft">{t.galleryPage.filters[item.category]}</p>
                  <h3 className="mt-1 font-bold text-white">{lang === "de" ? item.title_de : item.title_it}</h3>
                  {item.material && <p className="mt-1 text-xs text-slate-400">{item.material}</p>}
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </section>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-anthracite-dark/90 p-4 backdrop-blur-sm"
            onClick={() => setActive(null)}
            data-testid="gallery-modal"
          >
            <motion.div
              initial={{ scale: 0.94, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.94, y: 20 }}
              transition={{ duration: 0.3 }}
              className="relative max-h-[90vh] w-full max-w-4xl overflow-auto border border-white/15 bg-anthracite"
              onClick={(e) => e.stopPropagation()}
            >
              <button onClick={() => setActive(null)} className="absolute right-4 top-4 z-10 bg-anthracite-dark/80 p-2 text-white transition-colors hover:bg-royal" aria-label={t.galleryPage.close} data-testid="gallery-modal-close">
                <X size={20} />
              </button>
              <img src={fileUrl(active.image_url)} alt={lang === "de" ? active.title_de : active.title_it} className="max-h-[60vh] w-full object-cover" />
              <div className="p-8">
                <p className="font-mono-tech text-xs uppercase tracking-[0.3em] text-royal-soft">{t.galleryPage.filters[active.category]}</p>
                <h3 className="mt-2 text-2xl font-black uppercase tracking-tight text-white">{lang === "de" ? active.title_de : active.title_it}</h3>
                {active.material && <p className="mt-2 text-sm text-slate-400">{active.material}</p>}
                {active.is_placeholder && (
                  <p className="mt-4 border border-amber-400/40 bg-amber-400/10 px-4 py-3 text-xs text-amber-300" data-testid="gallery-modal-placeholder">
                    {lang === "de" ? active.note_de : active.note_it}
                  </p>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
